import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Skeleton
} from '@mui/material';
import {
  Search as SearchIcon,
  LinkedIn as LinkedInIcon,
  Article as ArticleIcon,
  FilterList as FilterIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import FeatureLockOverlay from '../../components/FeatureLockOverlay';
import api from '../../utils/api';
import LoadingScreen from '../../components/common/LoadingScreen';

// Import SVG as ReactComponent
import { ReactComponent as PostReactions } from '../post-reactions.svg';

// Import all profile data
import chatgptProfiles from './chatgpt_profiles.json';
import copywritingProfiles from './copywriting_profiles.json';
import digitalMarketingProfiles from './digital_marketing_profiles.json';
import emailMarketingProfiles from './email_marketing_profiles.json';
import humanResourceProfiles from './human_resource_profiles.json';
import leadGenProfiles from './lead_gen_profiles.json';
import leadershipProfiles from './leadership_profiles.json';
import personalBrandingProfiles from './personal_branding_profiles.json';
import productivityProfiles from './productivity_profiles.json';
import saasFoundersProfiles from './saas_founders_profiles.json';
import salesProfiles from './sales_profiles.json';
import startupsEntrepreneurshipProfiles from './startups_&_entrepreneurship_profiles.json';
import storyTellingProfiles from './story_telling_profiles.json';
import tiktokProfiles from './tiktok_profiles.json';
// Import new profile categories
import businessFinanceProfiles from './business_finance_profiles.json';
import cryptoWeb3Profiles from './crypto_web3_profiles.json';
import designProfiles from './design_profiles.json';
import ecommerceDtcProfiles from './ecommerce_dtc_profiles.json';
import mentalHealthProfiles from './mental_health_profiles.json';
import realEstateProfiles from './real_estate_profiles.json';
import seoProfiles from './seo_profiles.json';
import ventureCapitalProfiles from './venture_capital_profiles.json';

const categories = [
  { id: 'all', label: 'All Categories' },
  { id: 'chatgpt', label: 'ChatGPT', data: chatgptProfiles },
  { id: 'copywriting', label: 'Copywriting', data: copywritingProfiles },
  { id: 'digital-marketing', label: 'Digital Marketing', data: digitalMarketingProfiles },
  { id: 'email-marketing', label: 'Email Marketing', data: emailMarketingProfiles },
  { id: 'human-resources', label: 'Human Resources', data: humanResourceProfiles },
  { id: 'lead-generation', label: 'Lead Generation', data: leadGenProfiles },
  { id: 'leadership', label: 'Leadership', data: leadershipProfiles },
  { id: 'personal-branding', label: 'Personal Branding', data: personalBrandingProfiles },
  { id: 'productivity', label: 'Productivity', data: productivityProfiles },
  { id: 'saas-founders', label: 'SaaS Founders', data: saasFoundersProfiles },
  { id: 'sales', label: 'Sales', data: salesProfiles },
  { id: 'startups-entrepreneurship', label: 'Startups & Entrepreneurship', data: startupsEntrepreneurshipProfiles },
  { id: 'story-telling', label: 'Story Telling', data: storyTellingProfiles },
  { id: 'tiktok', label: 'TikTok', data: tiktokProfiles },
  // Add new categories
  { id: 'business-finance', label: 'Business & Finance', data: businessFinanceProfiles },
  { id: 'crypto-web3', label: 'Crypto & Web3', data: cryptoWeb3Profiles },
  { id: 'design', label: 'Design', data: designProfiles },
  { id: 'ecommerce-dtc', label: 'E-commerce & DTC', data: ecommerceDtcProfiles },
  { id: 'mental-health', label: 'Mental Health', data: mentalHealthProfiles },
  { id: 'real-estate', label: 'Real Estate', data: realEstateProfiles },
  { id: 'seo', label: 'SEO', data: seoProfiles },
  { id: 'venture-capital', label: 'Venture Capital', data: ventureCapitalProfiles }
];

const InfluencerCard = ({ influencer }) => {
  const [backgroundColor, setBackgroundColor] = useState('#f8fafc');

  const formatFollowerCount = (count) => {
    if (!count) return '0';
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    }
    if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count;
  };

  const extractDominantColor = useCallback((imageUrl) => {
    if (!imageUrl || imageUrl.includes('Default_pfp.svg')) {
      setBackgroundColor('#f8fafc');
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = 60;
        canvas.height = 60;
        
        ctx.drawImage(img, 0, 0, 60, 60);
        const imageData = ctx.getImageData(0, 0, 60, 60);
        const data = imageData.data;
        
        let r = 0, g = 0, b = 0;
        let pixelCount = 0;
        
        // Sample every 4th pixel for performance
        for (let i = 0; i < data.length; i += 16) {
          if (data[i + 3] > 128) { // Only non-transparent pixels
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            pixelCount++;
          }
        }
        
        if (pixelCount > 0) {
          r = Math.floor(r / pixelCount);
          g = Math.floor(g / pixelCount);
          b = Math.floor(b / pixelCount);
          
          // Create a very light version (90% opacity with white)
          const lightR = Math.floor(r + (255 - r) * 0.9);
          const lightG = Math.floor(g + (255 - g) * 0.9);
          const lightB = Math.floor(b + (255 - b) * 0.9);
          
          setBackgroundColor(`rgb(${lightR}, ${lightG}, ${lightB})`);
        } else {
          setBackgroundColor('#f8fafc');
        }
      } catch (error) {
        console.warn('Color extraction failed:', error);
        setBackgroundColor('#f8fafc');
      }
    };
    
    img.onerror = () => {
      setBackgroundColor('#f8fafc');
    };
    
    img.src = imageUrl;
  }, []);

  useEffect(() => {
    if (influencer.profile_picture_url) {
      extractDominantColor(influencer.profile_picture_url);
    }
  }, [influencer.profile_picture_url, extractDominantColor]);

  return (
    <Card sx={{ 
      height: '100%',
      minHeight: '280px',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        transform: 'translateY(-2px)'
      },
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid #e0e0e0',
      position: 'relative',
      backgroundColor: backgroundColor
    }}>
      <CardContent sx={{ 
        p: '16px !important',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          mb: 2
        }}>
          <Avatar
            src={influencer.profile_picture_url || "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2c/Default_pfp.svg/250px-Default_pfp.svg.png"}
            alt={influencer.name}
            sx={{ 
              width: 64,
              height: 64,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              bgcolor: 'white',
              '& img': {
                objectFit: 'cover'
              }
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600,
              fontSize: '1rem',
              mb: 0.5,
              color: '#000000',
              lineHeight: 1.2
            }}>
              {influencer.name}
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#666666'
            }}>
              <LinkedInIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {formatFollowerCount(influencer.follower_count)} followers
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          mb: 1.5
        }}>
          <Typography variant="body1" sx={{ 
            color: '#666666',
            mb: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
            fontSize: '0.8125rem'
          }}>
            {influencer.headline}
          </Typography>
          {influencer.location && (
            <Typography variant="body2" sx={{ 
              color: '#666666',
              fontSize: '0.875rem',
              mb: 1.5
            }}>
              {influencer.location}
            </Typography>
          )}
        </Box>

        <Box sx={{ 
          mt: 'auto', 
          display: 'flex',
          flexDirection: 'row',
          gap: 1
        }}>
          <Button
            variant="contained"
            startIcon={<LinkedInIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            href={influencer.profile_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              flex: 1,
              textTransform: 'none',
              fontWeight: 500,
              bgcolor: '#4890fe',
              '&:hover': {
                bgcolor: '#3a7ad9',
                boxShadow: 'none'
              },
              borderRadius: 2,
              boxShadow: 'none',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              height: '32px',
              minWidth: '80px',
              border: '1px solid #4890fe',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              '& .MuiButton-startIcon': {
                marginRight: { xs: '4px', sm: '8px' }
              }
            }}
          >
            View Profile
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArticleIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            href={`${influencer.profile_url}/recent-activity/all/`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              flex: 1,
              textTransform: 'none',
              fontWeight: 500,
              borderColor: '#E2E8F0',
              color: '#4A5568',
              '&:hover': {
                borderColor: '#E2E8F0',
                bgcolor: 'rgba(0, 0, 0, 0.04)',
                boxShadow: 'none'
              },
              borderRadius: 2,
              boxShadow: 'none',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              height: '32px',
              minWidth: '80px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              '& .MuiButton-startIcon': {
                marginRight: { xs: '4px', sm: '8px' }
              }
            }}
          >
            View Posts
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const Influencers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredInfluencers, setFilteredInfluencers] = useState([]);
  const [loadingInitialData, setLoadingInitialData] = useState(true);

  // New state for subscription check
  const [subscriptionStatusLoading, setSubscriptionStatusLoading] = useState(true);
  const [isFeatureAllowed, setIsFeatureAllowed] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState(null);

  // Plan IDs from environment variables
  const PRO_PLAN_ID = process.env.REACT_APP_PADDLE_PRO_MONTHLY;
  const PRO_YEARLY_ID = process.env.REACT_APP_PADDLE_PRO_YEARLY;
  const BUSINESS_PLAN_ID = process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY;
  const BUSINESS_YEARLY_ID = process.env.REACT_APP_PADDLE_BUSINESS_YEARLY;

  useEffect(() => {
    const checkSubscription = async () => {
      setSubscriptionStatusLoading(true);
      try {
        const response = await api.get('/api/subscriptions/status');
        const subscription = response.data.subscription;
        
        if (subscription && subscription.status === 'active') {
          setCurrentPlanId(subscription.price_id);
          if (
            subscription.price_id === PRO_PLAN_ID || 
            subscription.price_id === PRO_YEARLY_ID || 
            subscription.price_id === BUSINESS_PLAN_ID || 
            subscription.price_id === BUSINESS_YEARLY_ID
          ) {
            setIsFeatureAllowed(true);
          } else {
            setIsFeatureAllowed(false);
          }
        } else {
          setIsFeatureAllowed(false);
        }
      } catch (error) {
        console.error("Error fetching subscription status:", error);
        setIsFeatureAllowed(false);
      } finally {
        setSubscriptionStatusLoading(false);
      }
    };
    checkSubscription();
  }, [PRO_PLAN_ID, PRO_YEARLY_ID, BUSINESS_PLAN_ID, BUSINESS_YEARLY_ID]);

  useEffect(() => {
    // Only process influencers if the feature is allowed and subscription status has been checked
    if (isFeatureAllowed && !subscriptionStatusLoading) {
      setLoadingInitialData(true); // Start loading for influencer data processing
      let allInfluencers = [];
      if (selectedCategory === 'all') {
        allInfluencers = categories.reduce((acc, category) => {
    if (category.data) {
            return acc.concat(category.data);
    }
    return acc;
  }, []);
      } else {
        const categoryData = categories.find(cat => cat.id === selectedCategory)?.data;
        allInfluencers = categoryData || [];
      }

      const uniqueInfluencers = Array.from(new Set(allInfluencers.map(inf => inf.profile_url)))
        .map(url => allInfluencers.find(inf => inf.profile_url === url));
      
      setFilteredInfluencers(
        uniqueInfluencers.filter(influencer =>
          influencer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (influencer.headline && influencer.headline.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      );
      setLoadingInitialData(false); // Finish loading for influencer data processing
    } else if (!subscriptionStatusLoading && !isFeatureAllowed) {
      setLoadingInitialData(false); // Stop loading if feature not allowed
    }
  }, [searchTerm, selectedCategory, isFeatureAllowed, subscriptionStatusLoading]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Conditional rendering based on subscription status
  if (subscriptionStatusLoading) {
    return <LoadingScreen message="Loading Influencers..." />;
  }

  // Main component rendering logic
  const renderMainContent = () => (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Helmet>
        <title>Trending LinkedIn Influencers | Instation</title>
        <meta name="description" content="Discover and connect with influential LinkedIn thought leaders in your industry. Stay ahead of trends and insights from top professionals." />
      </Helmet>
      
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 600,
              color: '#2D3748',
              mb: 0.5
            }}>
              Trending Influencers
            </Typography>
            <Typography variant="body1" sx={{ color: '#718096' }}>
              Discover influential people from your chosen topics and industries. Use these insights to connect with thought leaders and stay ahead of trends.
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            alignItems: 'center'
          }}>
            <TextField
              placeholder="Search influencers..."
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              sx={{ 
                flex: { xs: '1 1 100%', md: 1 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#718096' }} />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ 
              minWidth: { xs: '100%', md: 200 }
            }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Category"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 1, 
        mb: 3 
      }}>
        {categories.map(category => (
          <Chip
            key={category.id}
            label={category.label}
            onClick={() => setSelectedCategory(category.id)}
            variant={selectedCategory === category.id ? "filled" : "outlined"}
            sx={{
              borderRadius: 2,
              bgcolor: selectedCategory === category.id ? '#4890fe' : 'transparent',
              color: selectedCategory === category.id ? 'white' : '#4A5568',
              borderColor: selectedCategory === category.id ? '#4890fe' : '#E2E8F0',
              '&:hover': {
                bgcolor: selectedCategory === category.id ? '#3a7ad9' : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          />
        ))}
      </Box>

      <Grid container spacing={3}>
        {filteredInfluencers.map((influencer) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={influencer.id}>
            <InfluencerCard influencer={influencer} />
          </Grid>
        ))}
      </Grid>

      {!loadingInitialData && filteredInfluencers.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            No more influencers to load
          </Typography>
        </Box>
      )}

      {filteredInfluencers.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          px: 2,
          bgcolor: '#f8fafc',
          borderRadius: 2,
          border: '1px solid #e2e8f0'
        }}>
          <Typography variant="h6" sx={{ color: '#4A5568', mb: 1 }}>
            No influencers found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try adjusting your search or category filters
          </Typography>
        </Box>
      )}
    </Container>
  );

  if (!isFeatureAllowed) {
    // Render minimal skeleton UI covering the viewport
    return (
      <Box sx={{ 
        position: 'relative', 
        height: 'calc(100vh - 64px)', // Adjust 64px based on your header/app bar height
        overflow: 'hidden'
      }}>
        {/* Apply opacity to the skeleton container */}
        <Box sx={{ opacity: 0.4, height: '100%' }}>
          <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Skeleton Header */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
              <Skeleton variant="text" width="30%" height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1, maxWidth: 400 }} />
                <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
              </Box>
            </Paper>
            
            {/* Skeleton Category Chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {Array.from(new Array(8)).map((_, index) => (
                <Skeleton key={index} variant="rectangular" width={100} height={32} sx={{ borderRadius: 4 }} />
              ))}
            </Box>

            {/* Skeleton Influencer Grid - Improved Layout */}
            <Grid container spacing={3}>
              {Array.from(new Array(8)).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Card sx={{ height: 320, borderRadius: 2, position: 'relative', overflow: 'visible' }}>
                    <Skeleton variant="rectangular" height={100} />
                    <Skeleton 
                      variant="circular" 
                      width={96} 
                      height={96} 
                      sx={{ 
                        position: 'absolute', 
                        top: 52, // Position avatar overlapping banner
                        left: 16, 
                        border: '3px solid #f0f0f0' // Mimic border 
                      }} 
                    />
                    <CardContent sx={{ pt: 8 }}> {/* Add padding top to account for avatar */}
                      <Skeleton variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="90%" sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="70%" sx={{ mb: 2 }} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
        
        {/* Overlay remains centered on top */}
        <FeatureLockOverlay featureName="Trending Influencers" currentPlan={currentPlanId} />
      </Box>
    );
  }
  
  // If feature is allowed, render content normally
  // Handle loading state for actual influencer data if the feature is allowed
  if (loadingInitialData && filteredInfluencers.length === 0 && isFeatureAllowed) { // Check isFeatureAllowed here
    return <LoadingScreen message="Loading Influencers..." />;
  }

  return renderMainContent();
};

export default Influencers; 